import HttpService from "./http";

export type PayoutMethodEnum = "STRIPE_CONNECT" | "BANK_TRANSFER" | "CRYPTO";

export const PayoutMethod = {
  STRIPE_CONNECT: "STRIPE_CONNECT" as PayoutMethodEnum,
  BANK_TRANSFER: "BANK_TRANSFER" as PayoutMethodEnum,
  CRYPTO: "CRYPTO" as PayoutMethodEnum,
} as const;

export type PayoutStatusEnum = "PENDING" | "PROCESSING" | "PAID" | "FAILED";

export const PayoutStatus = {
  PENDING: "PENDING" as PayoutStatusEnum,
  PROCESSING: "PROCESSING" as PayoutStatusEnum,
  PAID: "PAID" as PayoutStatusEnum,
  FAILED: "FAILED" as PayoutStatusEnum,
} as const;

export type PaymentStatusEnum =
  | "ACTIVE"
  | "CANCELED"
  | "ENDED"
  | "UNPAID"
  | "SUCCEEDED"
  | "REFUNDED"
  | "ERROR"
  | "PROCESSING";

export interface PaymentStepDTO {
  paymentStepIdentification: string;
  paymentDateTime: string;
  paymentStatusEnum: PaymentStatusEnum;
  paymentNextDate?: string | null;
  paymentDetailMap?: SubscriptionDetailDTO;
  paymentRequestUniqueLink?: string | null;

  amount_refunded?: number;
  refund_reason?: string;
}

export interface PayoutItemResponseDTO {
  payment_step_identification: string;
  payment_identification: string;
  amount: number;
  currency: string;
  payment_date_time: string;
}

export interface PayoutResponseDTO {
  payout_identification: string;
  entity_identification: string;
  payout_method: PayoutMethodEnum;
  gross_amount: number;
  platform_fee_rate: number;
  platform_fee: number;
  net_amount: number;
  currency: string;
  status: PayoutStatusEnum;
  provider_reference: string | null;
  payout_detail: any | null;
  period_start: string;
  period_end: string;
  payout_date_time: string | null;
  creation_date_time: string;
  items: PayoutItemResponseDTO[];
}

export interface CurrencyBalance {
  currency: string;
  gross_amount: number;
  estimated_fee: number;
  estimated_net_amount: number;
}

export interface BalanceResponseDTO {
  entity_identification: string;
  balances: CurrencyBalance[];
  unpaid_payments_count: number;
}

export interface PaymentStepPayoutStatusDTO {
  paymentStepIdentification: string;
  paymentIdentification: string;
  paymentStatus: PaymentStatusEnum;
  amount: number;
  currency: string;
  paymentDateTime: string;
  payoutIdentification: string | null;
  payoutStatus: string | null;
  payoutDateTime: string | null;
  includedInPayout: boolean;
}

export interface PaymodApiResponse<T> {
  status?: string;
  code?: string;
  message: string;
  element: T;
}

export interface InvoiceResponseDTO {
  invoiceNumber: string;
  invoiceUrlLink: string;
  invoicePDFLink: string;
}

export interface PaginatedPayoutResponseDTO {
  content: PayoutResponseDTO[];
  page: number;
  size: number;
  total_elements: number;
  total_pages: number;
  first: boolean;
  last: boolean;
}

export interface PayoutStatusUpdateDTO {
  status: PayoutStatusEnum;
  provider_reference?: string;
  payout_detail?: any;
}

export interface PayoutQueryOptions {
  page?: number;
  size?: number;
  status?: PayoutStatusEnum;
  method?: PayoutMethodEnum;
  sortBy?: "netAmount" | "creationDateTime";
  sortDir?: "asc" | "desc";
}

export type PayoutAccountStatusEnum =
  | "PENDING"
  | "ACTIVE"
  | "RESTRICTED"
  | "DISABLED"
  | "REJECTED"
  | "INACTIVE";

export type PlanTaxMode = "NONE" | "INCLUSIVE" | "EXCLUSIVE";

export interface ServicePlanDetailDTO {
  plan_code: string;
  plan_description: string;
  service_name: string;
  amount: number;
  recurrence: string;
  currency: string;
  starting_delay: number;
  is_public: boolean;
  grantable?: boolean;
  tax_mode: PlanTaxMode;
  tax_mode_settable?: boolean;
  provider_plan_identification: string;
  created_at?: string;
}

export interface PlanTaxOverviewDTO {
  plan_code: string;
  plan_description: string;
  entity_identification: string;
  service_id: number;
  service_name: string;
  provider_name: string;
  amount: number;
  currency: string;
  recurrence: string;
  is_public: boolean;
  tax_mode: PlanTaxMode;
  service_stripe_tax_code: string | null;
  provider_plan_identification: string;
}

export interface StripeTaxSettingsDTO {
  status: string;
  default_tax_behavior: string | null;
  default_tax_code: string | null;
  head_office_country: string | null;
  head_office_city: string | null;
  livemode: boolean;
  /** Empty means Stripe collects nothing, whatever the plans say. */
  registration_countries: string[];
}

export interface GrantPlanDTO {
  base_plan_identification: string;
  starting_delay?: number;
  duration_days?: number;
}

export interface GuaranteeRefundRequestDTO {
  plan_code: string;
  amount?: number;
  reason: string;
  request_id: string;
}

export interface GuaranteeRefundDTO {
  refund_id: string;
  amount_refunded: number;
  currency: string;
  status: string;
}

export interface TrialGrantResponseDTO {
  entity_identification: string;
  plan_code: string;
  plan_description: string;
  granted_date: string;
  expiration_date: string;
  starting_delay_days: number;
}

export type PaymentRecurrencyType =
  | "UNIQUE"
  | "MONTHLY"
  | "ANNUALLY"
  | "WEEKLY"
  | "DAILY";

export interface PaymentHistoryGroupedDTO {
  planCode: string;
  planDescription: string;
  payment_type?: PaymentRecurrencyType;
  paymentSteps: PaymentStepDTO[];
}

export interface SumPlanStatusEntryDTO {
  plan: string;
  description?: string;
  status?: PaymentStatusEnum;
  has_subscription?: boolean;
  cancel_at_period_end?: boolean;
  current_period_end?: string;
  last_payment?: { amount: number; currency: string; date: string };
}

export interface SumPlanStatusDTO {
  planList: SumPlanStatusEntryDTO[];
}

export interface SubscriptionDetailDTO {
  created?: string;
  default_payment_method?: string;
  price_currency?: string;
  price?: number;
  provider_status?: string;
  invoice_id?: string | null;
  payment_id?: string;
}

export interface StripeConnectAccountDetail {
  stripeAccountId: string;
}

export interface BankTransferAccountDetail {
  bank_name?: string;
  iban: string;
  bic?: string;
  account_holder: string;
  country?: string;
  bank_address?: string;
}

export interface CryptoAccountDetail {
  wallet_address: string;
  network?: string;
  currency?: string;
}

export type AccountDetail =
  | StripeConnectAccountDetail
  | BankTransferAccountDetail
  | CryptoAccountDetail;

export interface PayoutAccountResponseDTO {
  entity_identification: string;
  payout_method: PayoutMethodEnum;
  status: PayoutAccountStatusEnum;
  account_detail: AccountDetail;
  is_default: boolean;
  fee_rate: number;
  fixed_fee: number;
  minimum_payout: number;
  creation_date_time: string;
  update_date_time: string;
  stripe_dashboard_login_link?: string;
}

export interface PaymodHeapMemory {
  used: string;
  committed: string;
  max: string;
  usedBytes: number;
  maxBytes: number;
  usagePercent: number;
}

export interface PaymodNonHeapMemory {
  used: string;
  committed: string;
  usedBytes: number;
}

export interface PaymodRuntimeMemory {
  free: string;
  total: string;
  max: string;
}

export interface PaymodMemoryInfo {
  heap: PaymodHeapMemory;
  nonHeap: PaymodNonHeapMemory;
  runtime: PaymodRuntimeMemory;
}

export interface PaymodCpuInfo {
  cores: number;
  model: string;
  cpuUsage?: string;
}

export interface PaymodDatabaseInfo {
  status: string;
  url?: string;
  databaseProductName?: string;
  databaseProductVersion?: string;
  driverName?: string;
  driverVersion?: string;
  pingTimeMs?: number;
  poolSize?: number;
  poolMin?: number;
  activeConnections?: number;
  idleConnections?: number;
  totalConnections?: number;
  error?: string;
}

export interface PaymodThreadInfo {
  threadCount: number;
  peakThreadCount: number;
  daemonThreadCount: number;
}

export interface PaymodJvmInfo {
  vmName: string;
  vmVendor: string;
  vmVersion: string;
  specVersion: string;
  uptime: string;
  uptimeMs: number;
  startTime: string;
  inputArguments: string[];
  threads: PaymodThreadInfo;
}

export interface PaymodCacheDetails {
  size: number;
  type: string;
  hitCount?: number;
  missCount?: number;
  hitRate?: string;
  evictionCount?: number;
  loadCount?: number;
}

export interface PaymodCacheInfo {
  totalKeys: number;
  cacheCount: number;
  cacheNames: string[];
  caches: Record<string, PaymodCacheDetails>;
}

export interface PaymodServerMonitoringData {
  version: string;
  memory: PaymodMemoryInfo;
  cpu: PaymodCpuInfo;
  database: PaymodDatabaseInfo;
  cache: PaymodCacheInfo;
  jvm: PaymodJvmInfo;
  timestamp: string;
}

export interface PayoutAccountDTO {
  payout_method: PayoutMethodEnum;
  account_detail: AccountDetail;
}

export interface StripeOnboardingResponseDTO {
  onboarding_url: string;
  stripe_account_id: string;
  account_status: string;
}

export type RecurrencyEnum = "MONTHLY" | "ANNUALLY";

export const Recurrency = {
  MONTHLY: "MONTHLY" as RecurrencyEnum,
  ANNUALLY: "ANNUALLY" as RecurrencyEnum,
} as const;

export interface ServiceDeclarationDTO {
  service_name: string;
  description: string;
}

export interface ServiceDeclarationResponseDTO {
  serviceId: number;
  serviceName: string;
  productName: string;
  entityIdentification: string;
  stripeProductId: string;
  createdAt: string;
}

export interface PlanRegistrationDTO {
  amount: number;
  currency: PaymentRequestCurrencyEnum;
  recurrency: RecurrencyEnum;
  description: string;
}

export interface PlanRegistrationResponseDTO {
  plan_code: string;
  amount: number;
  currency: string;
  recurrency: RecurrencyEnum | "UNIQUE";
  description: string;
  stripe_price_id: string;
  created_at: string;
}

export interface ServiceWithPlansDTO {
  service_id: number;
  service_name: string;
  service_description: string;
  service_stripe_tax_code: string | null;
  entity_identification: string;
  stripe_product_id?: string;
  created_at?: string;
  plans: ServicePlanDetailDTO[];
}

export interface PaymentSearchRequestDTO {
  user_identifications?: string[];
  statuses?: string[];
  payment_types?: string[];
  recurrencies?: string[];
  plan_codes?: string[];
  amount_min?: number;
  amount_max?: number;
  payment_date_from?: string;
  payment_date_to?: string;
  next_payment_date_from?: string;
  next_payment_date_to?: string;
  page?: number;
  size?: number;
  sort_by?: string;
  sort_direction?: "ASC" | "DESC";
}

export interface PaymentSearchResultDTO {
  payment_identification: string;
  user_identification: string;
  payment_type: string;
  recurrency: string;
  plan_code: string;
  plan_amount: number;
  plan_currency: string;
  product_name: string;
  payment_step_identification: string;
  current_status: string;
  payment_step: string;
  payment_date: string;
  next_payment_date: string;
  stripe_invoice_id: string;
  stripe_subscription_id: string;
  refund_reason: string;
  payment_request_unique_link: string;
}

export interface PaymentSearchResponseDTO {
  results: PaymentSearchResultDTO[];
  page: number;
  size: number;
  total_elements: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface PaymentSearchFiltersDTO {
  entity_identification: string;
  available_statuses: string[];
  available_payment_types: string[];
  available_recurrencies: string[];
  available_plan_codes: string[];
  available_sort_fields: string[];
  payment_date_range: {
    earliest_payment: string;
    latest_payment: string;
  } | null;
  amount_range: {
    min_amount: number;
    max_amount: number;
    currency: string;
  } | null;
}

export interface UserSearchPaginationDTO {
  total: number;
  limit: number;
  offset: number;
  current_page: number;
  total_pages: number;
  has_next_page: boolean;
  has_prev_page: boolean;
}

export interface UserSearchResponseDTO {
  status: string;
  code: string;
  message: string;
  element: {
    search_term?: string;
    search_mode: "full_text" | "path";
    users: any[];
    pagination: UserSearchPaginationDTO;
    search_info?: {
      max_results_per_page: number;
      min_query_length: number;
      max_matches_per_user: number;
    };
  };
}

export interface FeeCalculationResponseDTO {
  payout_method: string;
  amount: number;
  currency: string;
  fee_rate_basis_points: number;
  fixed_fee_cents: number;
  percentage_fee_cents: number;
  total_fee_cents: number;
  equivalent_percent: number;
  net_amount_cents: number;
  fee_description: string;
}

export interface RecurringPaymentRequestDTO {
  plan_code: string;
  description: string;
  user_identification?: string;
  redirect_url?: string;
}

export type PaymentRequestCurrencyEnum = "EUR" | "USD" | "CHF";

export interface PaymentRequestDTO {
  description: string;
  amount: number;
  currency: PaymentRequestCurrencyEnum;
  user_identification?: string;
}

export interface PaymentRequestResponseDTO {
  entity_identification: string;
  user_identification: string | null;
  payment_request_datetime?: string;
  plan_code: string;
  payment_unique_link: string;
  description?: string;
  amount: number;
  currency: PaymentRequestCurrencyEnum;
  recurrency?: RecurrencyEnum | "UNIQUE";
  redirect_url?: string;
  payment_link: string;
  ticket_data?: {
    purchase_date: string;
    ticket_quantity: number;
    attendee_user_identifications: string[];
    attendee_emails: string[];
    status: string;
  };
  attendee_email_validation_status?: Record<string, boolean>;
  attendee_already_has_ticket?: Record<string, boolean>;
  purchaser_already_has_ticket?: boolean;
  ticket_context: {
    event_code: string;
    ticket_id: string;
    ticket_quantity: number;
    effective_quantity_for_payment: number;
    price_per_ticket: number;
    currency: string;
    total_amount: number;
  };
}

const PaymentModuleService = {
  getAllPayouts: (
    customerIdentification: string,
    entityIdentification: string,
    options?: PayoutQueryOptions
  ) => {
    const params = new URLSearchParams();
    if (options?.page !== undefined)
      params.append("page", options.page.toString());
    if (options?.size !== undefined)
      params.append("size", options.size.toString());
    if (options?.status) params.append("status", options.status);
    if (options?.method) params.append("method", options.method);
    if (options?.sortBy) params.append("sortBy", options.sortBy);
    if (options?.sortDir) params.append("sortDir", options.sortDir);

    const queryString = params.toString();
    const url = `/${customerIdentification}/${entityIdentification}/paymod/payouts${
      queryString ? `?${queryString}` : ""
    }`;

    return HttpService.get<PaymodApiResponse<PaginatedPayoutResponseDTO>>(url);
  },

  getPayoutDetails: (
    customerIdentification: string,
    entityIdentification: string,
    payoutIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<PayoutResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/payout/${payoutIdentification}/items`
    ),

  getPayoutByPaymentStep: (
    customerIdentification: string,
    entityIdentification: string,
    paymentStepIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<PayoutResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/by-payment-step/${paymentStepIdentification}`
    ),

  getPayoutsByPayment: (
    customerIdentification: string,
    entityIdentification: string,
    paymentIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<PayoutResponseDTO[]>>(
      `/${customerIdentification}/${entityIdentification}/paymod/by-payment/${paymentIdentification}`
    ),

  getPayoutAccount: (
    customerIdentification: string,
    entityIdentification: string,
    payoutIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<PayoutAccountResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/payout/${payoutIdentification}/account`
    ),

  getServerMonitoring: (
    customerIdentification: string,
    entityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<PaymodServerMonitoringData>>(
      `/${customerIdentification}/${entityIdentification}/paymod/server-monitoring`
    ),

  updatePayoutStatus: (
    customerIdentification: string,
    entityIdentification: string,
    payoutIdentification: string,
    updateData: PayoutStatusUpdateDTO
  ) =>
    HttpService.put<PaymodApiResponse<PayoutResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/payout/${payoutIdentification}/status`,
      updateData
    ),

  getPayoutAccounts: (
    customerIdentification: string,
    entityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<PayoutAccountResponseDTO[]>>(
      `/${customerIdentification}/${entityIdentification}/paymod/owner/payout-accounts`
    ),

  getPayoutAccountsForMonitoring: (
    customerIdentification: string,
    entityIdentification: string,
    targetEntityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<PayoutAccountResponseDTO[]>>(
      `/${customerIdentification}/${entityIdentification}/paymod/monitoring/payout-accounts/${targetEntityIdentification}`
    ),

  getEntityPayoutsForMonitoring: (
    customerIdentification: string,
    entityIdentification: string,
    targetEntityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<PayoutResponseDTO[]>>(
      `/${customerIdentification}/${entityIdentification}/paymod/monitoring/payouts/${targetEntityIdentification}`
    ),

  getEntityBalanceForMonitoring: (
    customerIdentification: string,
    entityIdentification: string,
    targetEntityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<BalanceResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/monitoring/balance/${targetEntityIdentification}`
    ),

  getPayoutsByEntity: (
    customerIdentification: string,
    entityIdentification: string,
    targetEntityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<PayoutResponseDTO[]>>(
      `/${customerIdentification}/${entityIdentification}/paymod/monitoring/by-entity/${targetEntityIdentification}`
    ),

  getServiceWithPlans: (
    customerIdentification: string,
    entityIdentification: string,
    targetEntityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<ServiceWithPlansDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/monitoring/entity/${targetEntityIdentification}/service-with-plans`
    ),

  getPaymentSearchFilters: (
    customerIdentification: string,
    entityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<PaymentSearchFiltersDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/payment-search/filters`
    ),

  searchPayments: (
    customerIdentification: string,
    entityIdentification: string,
    searchRequest: PaymentSearchRequestDTO
  ) =>
    HttpService.post<PaymodApiResponse<PaymentSearchResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/payment-search`,
      searchRequest
    ),

  combinedSearch: (
    customerIdentification: string,
    entityIdentification: string,
    body: {
      query?: string | string[];
      limit?: number;
      offset?: number;
      user_role?: string;
      user_type?: string;
      created_from?: string;
      created_to?: string;
      last_connection_from?: string;
      last_connection_to?: string;
      user_sort_by?: "CREATION_DATE" | "LAST_CONNECTION_DATE";
      user_sort_order?: "ASC" | "DESC";
      statuses?: string[];
      payment_types?: string[];
      recurrencies?: string[];
      plan_codes?: string[];
      amount_min?: number;
      amount_max?: number;
      payment_date_from?: string;
      payment_date_to?: string;
      page?: number;
      size?: number;
      sort_by?: string;
      sort_direction?: "ASC" | "DESC";
    }
  ) =>
    HttpService.post<any>(
      `/${customerIdentification}/${entityIdentification}/user/search/combined`,
      body
    ),

  createPayoutAccount: (
    customerIdentification: string,
    entityIdentification: string,
    accountData: PayoutAccountDTO
  ) =>
    HttpService.post<PaymodApiResponse<PayoutAccountResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/owner/payout-accounts`,
      accountData
    ),

  deletePayoutAccount: (
    customerIdentification: string,
    entityIdentification: string,
    method: PayoutMethodEnum
  ) =>
    HttpService.delete<PaymodApiResponse<void>>(
      `/${customerIdentification}/${entityIdentification}/paymod/owner/payout-accounts/${method}`
    ),

  updatePayoutAccount: (
    customerIdentification: string,
    entityIdentification: string,
    method: PayoutMethodEnum,
    accountData: PayoutAccountDTO
  ) =>
    HttpService.put<PaymodApiResponse<PayoutAccountResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/owner/payout-accounts/${method}`,
      accountData
    ),

  setDefaultPayoutAccount: (
    customerIdentification: string,
    entityIdentification: string,
    method: PayoutMethodEnum
  ) =>
    HttpService.post<PaymodApiResponse<PayoutAccountResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/owner/payout-accounts/${method}/default`,
      {}
    ),

  getStripeOnboardingLink: (
    customerIdentification: string,
    entityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<StripeOnboardingResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/owner/payout-accounts/stripe/onboarding-link`
    ),

  getStripeOnboardingRefreshLink: (
    customerIdentification: string,
    entityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<StripeOnboardingResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/owner/payout-accounts/stripe/onboarding-link`
    ),

  getEntityPendingBalance: (
    customerIdentification: string,
    entityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<BalanceResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/owner/balance`
    ),

  getEntityPayouts: (
    customerIdentification: string,
    entityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<PayoutResponseDTO[]>>(
      `/${customerIdentification}/${entityIdentification}/paymod/owner/payouts`
    ),

  requestPayout: (
    customerIdentification: string,
    entityIdentification: string
  ) =>
    HttpService.post<PaymodApiResponse<PayoutResponseDTO[]>>(
      `/${customerIdentification}/${entityIdentification}/paymod/owner/payouts`,
      {}
    ),

  getEntityPayoutDetails: (
    customerIdentification: string,
    entityIdentification: string,
    payoutIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<PayoutResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/owner/payouts/${payoutIdentification}`
    ),

  createPaymentRequest: (
    customerIdentification: string,
    entityIdentification: string,
    paymentRequestData: PaymentRequestDTO
  ) =>
    HttpService.post<PaymodApiResponse<PaymentRequestResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/payment-request`,
      paymentRequestData
    ),

  getAllPaymentRequests: (
    customerIdentification: string,
    entityIdentification: string,
    userIdentification?: string
  ) => {
    const params = new URLSearchParams();
    if (userIdentification) {
      params.append("user_identification", userIdentification);
    }

    const queryString = params.toString();
    const url = `/${customerIdentification}/${entityIdentification}/paymod/payment-request${
      queryString ? `?${queryString}` : ""
    }`;

    return HttpService.get<PaymodApiResponse<PaymentRequestResponseDTO[]>>(url);
  },

  deletePaymentRequest: (
    customerIdentification: string,
    entityIdentification: string,
    uniqueLink: string
  ) =>
    HttpService.delete<PaymodApiResponse<void>>(
      `/${customerIdentification}/${entityIdentification}/paymod/payment-request/${uniqueLink}`
    ),

  createPaymentRequestForRegistration: ({
    entityIdentification,
    data,
    token,
  }: {
    entityIdentification: string;
    data: {
      user_identification: string;
      form_id: string;
      redirect_url: string;
    };
    token: string;
  }) =>
    HttpService.post<PaymodApiResponse<PaymentRequestResponseDTO>>(
      `/${entityIdentification}/paymod/payment-request-registration`,
      data,
      {
        headers: {
          Authorization: token,
        },
      }
    ),

  declareService: (
    customerIdentification: string,
    entityIdentification: string
  ) =>
    HttpService.post<PaymodApiResponse<ServiceDeclarationResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/service-declaration`,
      {}
    ),

  updateRecurringPlan: (
    customerIdentification: string,
    entityIdentification: string,
    planCode: string,
    data: { description: string }
  ) =>
    HttpService.put<PaymodApiResponse<PlanRegistrationResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/recurring-plan/${planCode}`,
      data
    ),

  updateServiceDeclaration: (
    customerIdentification: string,
    entityIdentification: string,
    data: {
      service_name?: string;
      service_description?: string;
      service_link?: string;
    }
  ) =>
    HttpService.put<PaymodApiResponse<any>>(
      `/${customerIdentification}/${entityIdentification}/paymod/service-declaration`,
      data
    ),

  registerRecurringPlan: (
    customerIdentification: string,
    entityIdentification: string,
    planData: PlanRegistrationDTO
  ) =>
    HttpService.post<PaymodApiResponse<PlanRegistrationResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/recurring-plan`,
      planData
    ),

  getRecurringPlans: (
    customerIdentification: string,
    entityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<PlanRegistrationResponseDTO[]>>(
      `/${customerIdentification}/${entityIdentification}/paymod/recurring-plan`
    ),

  calculatePayoutFee: (
    customerIdentification: string,
    entityIdentification: string,
    payoutMethod: string,
    amount: number,
    currency: string
  ) =>
    HttpService.get<PaymodApiResponse<FeeCalculationResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/fee-calculation?payout_method=${payoutMethod}&amount=${amount}&currency=${currency}`
    ),

  deleteRecurringPlan: (
    customerIdentification: string,
    entityIdentification: string,
    planCode: string
  ) =>
    HttpService.delete<PaymodApiResponse<void>>(
      `/${customerIdentification}/${entityIdentification}/paymod/recurring-plan/${planCode}`
    ),

  createRecurringPaymentRequest: (
    customerIdentification: string,
    entityIdentification: string,
    data: RecurringPaymentRequestDTO
  ) =>
    HttpService.post<PaymodApiResponse<PaymentRequestResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/payment-request`,
      data
    ),

  cancelPaymentRequestSubscription: (
    customerIdentification: string,
    entityIdentification: string,
    uniqueLink: string,
    cancelImmediately: boolean = false
  ) =>
    HttpService.post<PaymodApiResponse<PaymentRequestResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/payment-request/${uniqueLink}/cancel?cancel_immediately=${cancelImmediately}`,
      {}
    ),

  refundPayment: (
    customerIdentification: string,
    entityIdentification: string,
    data: {
      payment_step_identification: string;
      refund_option: "FULL" | "PARTIAL";
      amount?: number;
      reason?: string;
    }
  ) =>
    HttpService.post<PaymodApiResponse<any>>(
      `/${customerIdentification}/${entityIdentification}/paymod/refund`,
      data
    ),

  getInvoiceLink: (
    customerIdentification: string,
    entityIdentification: string,
    invoiceId: string
  ) =>
    HttpService.get<PaymodApiResponse<InvoiceResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/invoice/${invoiceId}`
    ),

  getCardUpdateLink: (
    customerIdentification: string,
    entityIdentification: string,
    userIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<{ link: string }>>(
      `/${customerIdentification}/${entityIdentification}/paymod/card-update-link/${userIdentification}`
    ),

  getNonPublicPlans: (
    customerIdentification: string,
    entityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<ServicePlanDetailDTO[]>>(
      `/${customerIdentification}/${entityIdentification}/paymod/admin/non-public-plans`
    ),

  getPlansWithTaxMode: (
    customerIdentification: string,
    entityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<PlanTaxOverviewDTO[]>>(
      `/${customerIdentification}/${entityIdentification}/paymod/admin/plans-tax`
    ),

  getStripeTaxSettings: (
    customerIdentification: string,
    entityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<StripeTaxSettingsDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/admin/stripe-tax-settings`
    ),

  setPlanTaxMode: (
    customerIdentification: string,
    entityIdentification: string,
    planCode: string,
    taxMode: PlanTaxMode
  ) =>
    HttpService.put<PaymodApiResponse<unknown>>(
      `/${customerIdentification}/${entityIdentification}/paymod/admin/plan/${planCode}/tax-mode`,
      { tax_mode: taxMode }
    ),

  setServiceTaxCode: (
    customerIdentification: string,
    entityIdentification: string,
    serviceId: number,
    stripeTaxCode: string
  ) =>
    HttpService.put<PaymodApiResponse<unknown>>(
      `/${customerIdentification}/${entityIdentification}/paymod/admin/service/${serviceId}/tax-code`,
      { stripe_tax_code: stripeTaxCode }
    ),

  grantPrivatePlan: (
    customerIdentification: string,
    entityIdentification: string,
    targetEntityIdentification: string,
    data: GrantPlanDTO
  ) =>
    HttpService.post<PaymodApiResponse<TrialGrantResponseDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/admin/${targetEntityIdentification}/grant-eec`,
      data
    ),

  forceExpirePlan: (
    customerIdentification: string,
    entityIdentification: string,
    targetEntityIdentification: string,
    planCode: string
  ) =>
    HttpService.delete<PaymodApiResponse<string>>(
      `/${customerIdentification}/${entityIdentification}/paymod/admin/${targetEntityIdentification}/expire-plan/${planCode}`
    ),

  refundUnderGuarantee: (
    customerIdentification: string,
    entityIdentification: string,
    targetEntityIdentification: string,
    data: GuaranteeRefundRequestDTO
  ) =>
    HttpService.post<PaymodApiResponse<GuaranteeRefundDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/admin/${targetEntityIdentification}/guarantee-refunds`,
      data
    ),

  getAllSubscriptionHistoriesGroupedByPlan: (
    customerIdentification: string,
    entityIdentification: string,
    targetEntityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<PaymentHistoryGroupedDTO[]>>(
      `/${customerIdentification}/${entityIdentification}/paymod/admin/${targetEntityIdentification}/subscription-histories`
    ),

  getSumPlanStatus: (
    customerIdentification: string,
    entityIdentification: string,
    targetEntityIdentification: string
  ) =>
    HttpService.get<PaymodApiResponse<SumPlanStatusDTO>>(
      `/${customerIdentification}/${entityIdentification}/paymod/admin/${targetEntityIdentification}/sum-plan-status`
    ),

  cancelSumSubscriptionAtPeriodEnd: (
    customerIdentification: string,
    entityIdentification: string,
    targetEntityIdentification: string,
    planCode: string
  ) =>
    HttpService.post<PaymodApiResponse<any>>(
      `/${customerIdentification}/${entityIdentification}/paymod/admin/${targetEntityIdentification}/cancel-at-period-end/${planCode}`,
      {}
    ),

  resumeSumSubscription: (
    customerIdentification: string,
    entityIdentification: string,
    targetEntityIdentification: string,
    planCode: string
  ) =>
    HttpService.post<PaymodApiResponse<any>>(
      `/${customerIdentification}/${entityIdentification}/paymod/admin/${targetEntityIdentification}/resume-subscription/${planCode}`,
      {}
    ),

  getEventAvailability: (entityIdentification: string, eventCode: string) =>
    HttpService.get<{
      status: string;
      message: string;
      element: {
        event_code: string;
        event_name?: string;
        event_description?: string;
        event_image_url?: string;
        tickets: Record<
          string,
          {
            ticket_id: string;
            name: string;
            max_tickets: number | null;
            sold_count: number;
            available_count: number | null;
            price: number;
            currency: string;
            min_per_order: number;
            max_per_order: number;
            show_remaining_tickets: boolean;
            sale_start_date?: string;
            sale_end_date?: string;
          }
        >;
      };
    }>(`/${entityIdentification}/event/${eventCode}/availability`),

  validateAttendees: (
    entityIdentification: string,
    data: {
      user_identification: string;
      event_code: string;
      ticket_id: string;
      additional_attendees?: Array<{
        first_name: string;
        last_name: string;
        user_email: string;
      }>;
    }
  ) =>
    HttpService.post<{
      status: string;
      message: string;
      element: {
        can_proceed: boolean;
        purchaser_already_has_ticket: boolean;
        purchaser_email: string | null;
        effective_quantity: number;
        valid_attendees: Array<{
          first_name: string;
          last_name: string;
          user_email: string;
          user_identification?: string;
        }>;
        price_in_cents: number;
        price_per_ticket: number;
        currency: string;
        warnings?: Record<string, Record<string, string>>;
        info?: Record<string, Record<string, string>>;
      };
    }>(`/${entityIdentification}/event/validate-attendees`, data),

  reserveTickets: (
    entityIdentification: string,
    data: {
      user_identification: string;
      event_code: string;
      ticket_id: string;
      attendee_emails?: Array<
        string | { user_email: string; first_name?: string; last_name?: string }
      >;
    }
  ) =>
    HttpService.post<{
      status: string;
      message: string;
      element: {
        reservation_id: string;
        expires_at: string;
        quantity_reserved: number;
        price_in_cents: number;
        currency: string;
        time_remaining_seconds: number;
        existing_reservation?: boolean;
      };
    }>(`/${entityIdentification}/event/reserve-tickets`, data),

  resendAttendeeValidationEmail: (
    entityIdentification: string,
    data: { user_email: string }
  ) =>
    HttpService.post<{
      status: string;
      message: string;
      element: {
        email_sent?: boolean;
        already_validated?: boolean;
        user_email?: string;
      };
    }>(`/${entityIdentification}/event/resend-validation-email`, data),

  createPaymentRequestForReservation: (
    entityIdentification: string,
    data: {
      user_identification: string;
      reservation_id: string;
      redirect_url?: string;
    }
  ) =>
    HttpService.post<{
      status: string;
      message: string;
      element: {
        payment_link?: string;
        payment_unique_link?: string;
        payment_url?: string;
        amount_cents?: number;
        currency?: string;
        reservation_id: string;
        time_remaining_seconds?: number;
        tickets_registered?: boolean;
        quantity?: number;
        redirect_url?: string;
      };
    }>(`/${entityIdentification}/event/payment-request-reservation`, data),
};

export default PaymentModuleService;
